import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, Stack } from '@mui/material'
import React, { FunctionComponent, useContext, useEffect, useState } from 'react'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BuildingPeriod } from '../../../../shared/interfaces/billing-period';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { IEditFileUploadLineVehicle, IFileUploadLineVehicle, IValidationError } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { getSelectableSubsidiaries } from '../../../../shared/utils/subsidiary-utils';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import UserDetailsService from '../../../../shared/services/user-details/user-details.service';
import { IUserDetails } from '../../../../shared/services/user-details/user-details.model';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model';
import { formatTransaction } from '../../../../shared/utils/format-post-utils';

export const EditUploadLineVehicleDialog: FunctionComponent<{
  editUploadLine: IFileUploadLineVehicle,
  open: boolean,
  closeHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLineVehicle) => void,
  isSubmitting: boolean,
  validationErrors?: IValidationError[],
  errorCode?: number,
  errorMessage?: string,
}> = ({
  editUploadLine,
  open,
  closeHandler,
  submitHandler,
  isSubmitting,
  validationErrors,
  errorCode,
  errorMessage
}) => {
    //Services
    const subsidiaryService = SubsidiaryService.getInstance();
    const userDetailsService = new UserDetailsService();
    const customFieldService = new CustomFieldService()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    const userDetailsContext = useContext(UserDetailsContext)
    const organisationDetails = userDetailsContext.userDetails?.organisation
    //State
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>()
    const [openStartDate, setOpenStartDate] = useState(false);
    const [users, setUsers] = React.useState<IUserDetails[]>([]);
    //Variables
    const ownership: string[] = [
      'Leased',
      'Owned (Using)',
      // 'Owned (Leased Out)'
    ]
    //Formik
    const vehicleSchema = Yup.object().shape({
      vinNumber: Yup.string().required(`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} identification number is required`),
      customUniqueId: Yup.string().required(`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} registration is required`),
      subsidiaryId: Yup.string().required(`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} is required`),
      dataEntryPeriod: Yup.string().nullable(),
      vehicleType: Yup.string().required(`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} type is required and must be either "Passenger Vehicle" or "Delivery Vehicle"`),
      assignedUserId: Yup.string().nullable(),
      startDate: Yup.string().required('Start date is required'),
      ownership: Yup.string().required('Ownership status is required and must be either "Leased" or "Owned (Using)"'),
      vehicleReference: Yup.string().nullable(),
      ...(customFields && customFields.length > 0 ? customFields.reduce((acc, field) => {
        if (field.isMandatory) {
          acc[field.id] = Yup.string().required(`${field.fieldName} is mandatory`);
        } else {
          acc[field.id] = Yup.string().nullable();
        }
        return acc;
      }, {} as any) : {}),
    })
    const formik = useFormik<IEditFileUploadLineVehicle & Record<string, string | number | boolean | null | undefined>>({
      initialValues: {
        vinNumber: editUploadLine.vinNumber,
        customUniqueId: editUploadLine.customUniqueId,
        subsidiaryId: editUploadLine.subsidiaryId,
        dataEntryPeriod: editUploadLine.dataEntryPeriod,
        vehicleType: editUploadLine.vehicleType,
        startDate: editUploadLine.startDate,
        ownership: editUploadLine.ownership,
        vehicleReference: editUploadLine.vehicleReference,
        assignedUserId: editUploadLine.assignedUserId,
      },
      validationSchema: vehicleSchema,
      validateOnMount: true,
      onSubmit: (values) => {
        const response = formatTransaction(values, '', true, customFields)
        submitHandler(editUploadLine.id, response)
      }
    });

    useEffect(() => {
      formik.setValues({
        vinNumber: editUploadLine.vinNumber,
        customUniqueId: editUploadLine.customUniqueId,
        dataEntryPeriod: editUploadLine.dataEntryPeriod,
        vehicleType: editUploadLine.vehicleType,
        startDate: editUploadLine.startDate,
        ownership: editUploadLine.ownership,
        subsidiaryId: editUploadLine.subsidiaryId,
        vehicleReference: editUploadLine.vehicleReference,
        assignedUserId: editUploadLine.assignedUserId,
      })
      subsidiaryService.getAll()
        .then(response => {
          setSubsidiaries(response)
        })
        .catch(err => console.log(err))

      userDetailsService.getAllUserDetails(`?status=active`)
        .then((response) => {
          setUsers(response.filter(user => !(user.inviteSent === true && user.inviteAccepted === false)))
        })
        .catch(err => {
          console.log(err)
        })

      customFieldService.getAllActive(`&entryType=vehicle`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))
    }, [])


    useEffect(() => {
      if (customFields && customFields.length && (editUploadLine as any)?.customFieldValues) {
        customFields.forEach(field => {
          const matchingValue = (editUploadLine as any).customFieldValues.find((v: any) => v.customFieldId === field.id)?.value ?? ''
          formik.setFieldValue(field.id, matchingValue, false)
        })
      }
    }, [customFields, editUploadLine])


    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form'>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            Edit Uploaded Data
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='vinNumber'>{screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Identification Number</InputLabel>
              <OutlinedInput inputProps={{ min: 0 }} id='vinNumber' label={`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Identification Number`} name='vinNumber'
                value={formik.values.vinNumber}
                onChange={formik.handleChange}
                error={formik.touched.vinNumber && Boolean(formik.errors.vinNumber)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.vinNumber && formik.errors.vinNumber &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.vinNumber}</FormHelperText>
              }
              {validationErrors && validationErrors.map((error, index) =>
                error.code === 1 && (
                  <FormHelperText key={index} sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{error.message}</FormHelperText>
                )
              )}
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='customUniqueId'>{screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Registration</InputLabel>
              <OutlinedInput inputProps={{ min: 0 }} id='customUniqueId' label={`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Registration`} name='customUniqueId'
                value={formik.values.customUniqueId}
                onChange={formik.handleChange}
                error={formik.touched.customUniqueId && Boolean(formik.errors.customUniqueId)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.customUniqueId && formik.errors.customUniqueId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.customUniqueId}</FormHelperText>
              }
              {validationErrors && validationErrors.map((error, index) =>
                error.code === 2 && (
                  <FormHelperText key={index} sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{error.message}</FormHelperText>
                )
              )}
              {(errorCode === 1 || errorCode === 2) && errorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='vehicleReference'>{screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Reference</InputLabel>
              <OutlinedInput inputProps={{ min: 0 }} id='vehicleReference' label={`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Reference`} name='vehicleReference'
                value={formik.values.vehicleReference}
                onChange={formik.handleChange}
                error={formik.touched.vehicleReference && Boolean(formik.errors.vehicleReference)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.vehicleReference && formik.errors.vehicleReference &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.vehicleReference}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='ownership'> Ownership</InputLabel>
              <Select
                id='ownership' name='ownership' label='Ownership'
                value={formik.values.ownership}
                onChange={formik.handleChange}
                error={formik.touched.ownership && Boolean(formik.errors.ownership)}
                fullWidth
                sx={{ color: 'text.primary' }}>
                {ownership.map((ownership, index) => (
                  <MenuItem key={index}
                    value={ownership}>{ownership}</MenuItem>
                ))}
              </Select>
              {formik.touched.ownership && formik.errors.ownership &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.ownership}</FormHelperText>
              }
              {validationErrors && validationErrors.map((error, index) =>
                error.code === 5 && (
                  <FormHelperText key={index} sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{error.message}</FormHelperText>
                )
              )}
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='dataEntryPeriod'>Frequency of Data Entry</InputLabel>
              <Select
                id='dataEntryPeriod' label='Frequency of Data Entry' name='dataEntryPeriod'
                value={formik.values.dataEntryPeriod}
                onChange={formik.handleChange}
                error={formik.touched.dataEntryPeriod && Boolean(formik.errors.dataEntryPeriod)}
                sx={{ color: 'text.primary', }}>
                {Object.values(BuildingPeriod).map(period => (
                  <MenuItem key={period} value={period}>{period.charAt(0).toUpperCase() + period.slice(1)}</MenuItem>
                ))}
              </Select>
              {formik.touched.dataEntryPeriod && formik.errors.dataEntryPeriod &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.dataEntryPeriod}</FormHelperText>
              }
              {validationErrors && validationErrors.map((error, index) =>
                error.code === 6 && (
                  <FormHelperText key={index} sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{error.message}</FormHelperText>
                )
              )}
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='vehicleType'>{screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Type</InputLabel>
              <Select
                id='vehicleType' label={`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} Type`} name='vehicleType'
                value={formik.values.vehicleType}
                onChange={formik.handleChange}
                error={formik.touched.vehicleType && Boolean(formik.errors.vehicleType)}
                sx={{ color: 'text.primary', }}>
                <MenuItem value={'Passenger vehicle'}>{'Passenger vehicle'}</MenuItem>
                <MenuItem value={'Delivery vehicle'}>{'Delivery vehicle'}</MenuItem>
              </Select>
              {formik.touched.vehicleType && formik.errors.vehicleType &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.vehicleType}</FormHelperText>
              }
              {validationErrors && validationErrors.map((error, index) =>
                error.code === 7 && (
                  <FormHelperText key={index} sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{error.message}</FormHelperText>
                )
              )}
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  value={formik.values.startDate ? moment(formik.values.startDate) : null}
                  onChange={(date) => formik.setFieldValue('startDate', date?.format('YYYY-MM-DD'))}
                  format='DD/MM/YYYY'
                  open={openStartDate}
                  onOpen={() => setOpenStartDate(true)}
                  onClose={() => setOpenStartDate(false)}
                  slotProps={{
                    textField: {
                      label: 'Start Date',
                      onClick: () => setOpenStartDate(true),
                      error: formik.touched.startDate && Boolean(formik.errors.startDate),
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </LocalizationProvider>
              {formik.touched.startDate && formik.errors.startDate &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.startDate ? <div>{formik.errors.startDate as string}</div> : null}
                </FormHelperText>
              }
              {validationErrors && validationErrors.map((error, index) =>
                error.code === 4 && (
                  <FormHelperText key={index} sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{error.message}</FormHelperText>
                )
              )}
            </FormControl>
            {subsidiaries && (organisationDetails?.enableSubsidiaries || subsidiaries.length > 1) &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='subsidiaryId'>{screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })}</InputLabel>
                <Select
                  id='subsidiaryId' label={screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} name='subsidiaryId'
                  value={formik.values.subsidiaryId}
                  onChange={formik.handleChange}
                  error={formik.touched.subsidiaryId && Boolean(formik.errors.subsidiaryId)}
                  sx={{ color: 'text.primary', }}>
                  {getSelectableSubsidiaries(subsidiaries, formik.values.subsidiaryId).sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) =>
                    <MenuItem key={index} value={subsidiary.id}>{subsidiary.name}</MenuItem>
                  )}
                </Select>
                {formik.touched.subsidiaryId && formik.errors.subsidiaryId &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.subsidiaryId ? <div>{formik.errors.subsidiaryId as string}</div> : null}
                  </FormHelperText>
                }
                {validationErrors && validationErrors.map((error, index) =>
                  error.code === 3 && (
                    <FormHelperText key={index} sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{error.message}</FormHelperText>
                  )
                )}
              </FormControl>}
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='assignedUserId'>Assigned User</InputLabel>
              <Select
                id='assignedUserId' label='Assigned User' name='assignedUserId'
                value={formik.values.assignedUserId}
                onChange={formik.handleChange}
                error={formik.touched.assignedUserId && Boolean(formik.errors.assignedUserId)}
                sx={{ color: 'text.primary', }}>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {users.sort((a, b) => a.firstName.localeCompare(b.firstName)).map((user, index) => (
                  <MenuItem key={index} value={user.id}>{user.firstName} {user.surname}</MenuItem>
                ))}
              </Select>
              {formik.touched.assignedUserId && formik.errors.assignedUserId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.assignedUserId}</FormHelperText>
              }
            </FormControl>
            {customFields && customFields.length > 0 && customFields.map((customField, index) => {
              return (
                <Stack direction='row' spacing={2} key={index} sx={{ mt: 2 }}>
                  <FormControl fullWidth variant='outlined'>
                    <InputLabel htmlFor={customField.id}> {customField.fieldName}</InputLabel>
                    <OutlinedInput id={customField.id} label={customField.fieldName} name={customField.id}
                      type={customField.fieldType === 'number' ? 'number' : 'text'}
                      value={formik.values[customField.id]}
                      onChange={formik.handleChange}
                      error={formik.touched[customField.id] && Boolean(formik.errors[customField.id])}
                      sx={{ color: 'text.primary' }}></OutlinedInput>
                  </FormControl>
                  {formik.touched[customField.id] && formik.errors[customField.id] &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors[customField.id] ? <div>{formik.errors[customField.id] as string}</div> : null}
                    </FormHelperText>
                  }
                </Stack>
              )
            })}
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }